import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import inspectionService from '../../services/inspectionService';
import projectService from '../../services/projectService';
import { CATEGORY_LABEL } from './categoryOptions';
import './TaskExecution.css';

// UI-facing statuses map to the backend's checklist item status enum.
const STATUS_TO_BACKEND = {
  compliant: 'compliant',
  'non-compliant': 'non_compliant',
  'n/a': 'not_applicable',
};

const TaskExecution = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  const category = searchParams.get('category');

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [checklistStatus, setChecklistStatus] = useState([]);

  const [location, setLocation] = useState('');
  const [photos, setPhotos] = useState([]);
  const [overallFindings, setOverallFindings] = useState('');
  const [recommendations, setRecommendations] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [gpsLocation, setGpsLocation] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [isCapturingGPS, setIsCapturingGPS] = useState(false);

  const loadChecklist = useCallback(async () => {
    if (!projectId || !category) {
      setLoadError('Missing project or category. Please start a new inspection from the Inspections page.');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setLoadError(null);
      const [checklistData, projectData] = await Promise.all([
        inspectionService.getChecklist({ projectId, category }),
        projectService.getById(projectId).catch(() => null),
      ]);
      const items = checklistData.items || [];
      setChecklistStatus(items.map((item) => ({
        ...item,
        status: 'pending',
        notes: '',
        numberValue: '',
        booleanValue: 'true',
        selectValue: '',
        // Deliberately blank, even when the parameter has a configured
        // default severity - severity drives how urgently a finding gets
        // treated, so whoever is marking something Non-Compliant has to
        // actually think about and choose it every time, not just inherit
        // whatever was silently pre-filled. The configured default (if any)
        // is still shown as a one-click suggestion below the field.
        severity: '',
      })));
      setProject(projectData?.project || null);
    } catch (error) {
      console.error('Failed to load inspection checklist:', error);
      setLoadError(error.response?.data?.error || 'Failed to load checklist');
    } finally {
      setLoading(false);
    }
  }, [projectId, category]);

  useEffect(() => {
    loadChecklist();
  }, [loadChecklist]);

  const handleChecklistItemChange = (index, status) => {
    setChecklistStatus((prev) => prev.map((item, i) => (i === index ? { ...item, status } : item)));
  };

  const handleNotesChange = (index, notes) => {
    setChecklistStatus((prev) => prev.map((item, i) => (i === index ? { ...item, notes } : item)));
  };

  const handleSeverityChange = (index, severity) => {
    setChecklistStatus((prev) => prev.map((item, i) => (i === index ? { ...item, severity } : item)));
  };

  // Entering a measured value for a numeric item auto-suggests Compliant/
  // Non-Compliant by comparing against the parameter's own configured
  // target range (mirrors the backend's computeEntryStatus) - the inspector
  // can still click a different status afterward to override it.
  const handleNumberValueChange = (index, numberValue) => {
    setChecklistStatus((prev) => prev.map((item, i) => {
      if (i !== index) return item;
      const next = { ...item, numberValue };
      const parsed = parseFloat(numberValue);
      if (numberValue === '' || Number.isNaN(parsed)) return next;
      const min = item.targetMin;
      const max = item.targetMax;
      if (min === null && max === null) return next;
      const withinRange = (min === null || parsed >= min) && (max === null || parsed <= max);
      next.status = withinRange ? 'compliant' : 'non-compliant';
      return next;
    }));
  };

  const handleBooleanValueChange = (index, booleanValue) => {
    setChecklistStatus((prev) => prev.map((item, i) => (i === index ? { ...item, booleanValue } : item)));
  };

  const handleSelectValueChange = (index, selectValue) => {
    setChecklistStatus((prev) => prev.map((item, i) => (i === index ? { ...item, selectValue } : item)));
  };

  // Builds the `value` payload for a checklist item from its captured
  // typed input, matching the shape MonitoringEntry expects ({number}/
  // {boolean}/{select}) - undefined when nothing was actually captured
  // (e.g. a text-type item, which relies on the free-text notes instead).
  const buildItemValue = (item) => {
    switch (item.dataType) {
      case 'number':
        return item.numberValue !== '' ? { number: parseFloat(item.numberValue) } : undefined;
      case 'boolean':
        return { boolean: item.booleanValue === 'true' };
      case 'select':
        return item.selectValue ? { select: item.selectValue } : undefined;
      default:
        return undefined;
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setPhotos((prev) => [...prev, ...newPhotos]);
    e.target.value = '';
  };

  const removePhoto = (index) => {
    setPhotos((prev) => {
      const next = [...prev];
      URL.revokeObjectURL(next[index].preview);
      next.splice(index, 1);
      return next;
    });
  };

  const captureGPSLocation = () => {
    setIsCapturingGPS(true);
    setGpsError(null);

    if (!navigator.geolocation) {
      setGpsError('Geolocation is not supported by your browser');
      setIsCapturingGPS(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGpsLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date().toISOString()
        });
        setIsCapturingGPS(false);
      },
      (error) => {
        let errorMessage = 'Unable to retrieve location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location services.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
          default:
            errorMessage = 'An unknown error occurred.';
        }
        setGpsError(errorMessage);
        setIsCapturingGPS(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const getCompletionStats = () => {
    const total = checklistStatus.length;
    const compliant = checklistStatus.filter((i) => i.status === 'compliant').length;
    const nonCompliant = checklistStatus.filter((i) => i.status === 'non-compliant').length;
    const notApplicable = checklistStatus.filter((i) => i.status === 'n/a').length;
    const pending = total - compliant - nonCompliant - notApplicable;

    return {
      total, compliant, nonCompliant, notApplicable, pending,
    };
  };

  const stats = getCompletionStats();
  const completionPercentage = stats.total
    ? Math.round(((stats.total - stats.pending) / stats.total) * 100)
    : 0;

  const missingSeverityCount = checklistStatus.filter((i) => i.status === 'non-compliant' && !i.severity).length;

  const handleSubmit = async () => {
    if (stats.total === 0 || stats.pending > 0) return;
    if (missingSeverityCount > 0) {
      toast.error(`Please choose a severity for ${missingSeverityCount === 1 ? 'the non-compliant item' : `all ${missingSeverityCount} non-compliant items`} before submitting`);
      return;
    }

    setIsSubmitting(true);
    try {
      const checklistPayload = checklistStatus.map((item) => ({
        parameterId: item.parameterId,
        text: item.text,
        category: item.category,
        status: STATUS_TO_BACKEND[item.status],
        notes: item.notes || null,
        value: buildItemValue(item),
        // Only meaningful for non-compliant items (the backend only reads it
        // when auto-raising a finding), but harmless to always include.
        severity: item.severity,
      }));

      // overallCompliance is not sent - the backend always derives it from
      // the checklist results, so it can't drift from the actual findings.
      const { inspection, findingsRaised, entriesRecorded } = await inspectionService.create({
        projectId,
        category,
        location: location || null,
        checklist: checklistPayload,
        gpsLocation,
        overallFindings: overallFindings || null,
        recommendations: recommendations || null,
      });

      // Upload any staged photos as evidence attached to the new inspection.
      for (const photo of photos) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await inspectionService.uploadEvidence(inspection.id, photo.file, 'photo');
        } catch (uploadError) {
          console.error('Failed to upload inspection photo:', uploadError);
        }
      }

      const parts = ['Inspection submitted'];
      if (findingsRaised > 0) parts.push(`${findingsRaised} compliance finding${findingsRaised === 1 ? '' : 's'} raised`);
      if (entriesRecorded > 0) parts.push(`${entriesRecorded} measurement${entriesRecorded === 1 ? '' : 's'} logged to monitoring`);
      toast.success(parts.join(' - '));
      navigate('/inspections');
    } catch (error) {
      console.error('Failed to submit inspection:', error);
      toast.error(error.response?.data?.error || 'Failed to submit inspection');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="task-execution-page">
        <div className="empty-state">
          <i className="fas fa-spinner fa-spin"></i>
          <h3>Loading checklist...</h3>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="task-execution-page">
        <div className="empty-state">
          <i className="fas fa-exclamation-circle"></i>
          <h3>{loadError}</h3>
          <button className="btn-primary-green" onClick={() => navigate('/inspections')}>
            Back to Inspections
          </button>
        </div>
      </div>
    );
  }

  if (checklistStatus.length === 0) {
    return (
      <div className="task-execution-page">
        <div className="empty-state">
          <i className="fas fa-clipboard-list"></i>
          <h3>No checklist items configured</h3>
          <p>
            This project has no active monitoring parameters for {CATEGORY_LABEL[category] || category}.
            Configure some first.
          </p>
          <button className="btn-primary-green" onClick={() => navigate('/monitoring/parameters/new')}>
            <i className="fas fa-sliders-h"></i> Configure a Monitoring Parameter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="task-execution-page">
      {/* Header */}
      <div className="execution-header">
        <button className="back-btn" onClick={() => navigate('/inspections')}>
          <i className="fas fa-arrow-left"></i> Back to Inspections
        </button>
        <div className="header-info">
          <h1>New Inspection</h1>
          <div className="task-meta">
            <span><i className="fas fa-folder"></i> {project?.name || 'Project'}</span>
            <span><i className="fas fa-tag"></i> {CATEGORY_LABEL[category] || category}</span>
          </div>
        </div>
      </div>

      {/* Location - only asked when the project has configured named
          sub-locations (see Projects > Edit Project > Sub-Locations).
          Single-location projects already have their address captured at
          project setup, so there's nothing meaningful to ask here every
          time an inspection is submitted. */}
      {project?.locations?.length > 0 && (
        <div className="findings-section">
          <h2><i className="fas fa-map-marker-alt"></i> Location</h2>
          <select
            className="findings-textarea"
            style={{ height: 'auto' }}
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          >
            <option value="">Not specific to one location</option>
            {project.locations.map((loc) => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>
        </div>
      )}

      {/* Progress Bar */}
      <div className="progress-section">
        <div className="progress-header">
          <span>Inspection Progress</span>
          <span className="progress-percentage">{completionPercentage}%</span>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${completionPercentage}%` }}></div>
        </div>
        <div className="progress-stats">
          <div className="stat compliant">
            <i className="fas fa-check-circle"></i> {stats.compliant} Compliant
          </div>
          <div className="stat non-compliant">
            <i className="fas fa-times-circle"></i> {stats.nonCompliant} Non-Compliant
          </div>
          <div className="stat n-a">
            <i className="fas fa-ban"></i> {stats.notApplicable} N/A
          </div>
          <div className="stat pending">
            <i className="fas fa-clock"></i> {stats.pending} Pending
          </div>
        </div>
      </div>

      {/* Checklist */}
      <div className="checklist-section">
        <h2><i className="fas fa-clipboard-list"></i> Inspection Checklist</h2>
        <div className="checklist-items">
          {checklistStatus.map((item, index) => (
            <div key={item.parameterId} className="checklist-item">
              <div className="item-header">
                <span className="item-number">{index + 1}</span>
                <span className="item-text">{item.text}{item.unit ? ` (${item.unit})` : ''}</span>
              </div>

              <div className="item-controls">
                {item.dataType === 'number' && (
                  <div className="item-value-input">
                    <label>
                      Measured value {item.unit ? `(${item.unit})` : ''}
                      {(item.targetMin !== null || item.targetMax !== null) && (
                        <span className="item-target-hint">
                          {' '}· Target: {item.targetMin !== null ? item.targetMin : '–'}–{item.targetMax !== null ? item.targetMax : '–'}
                        </span>
                      )}
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={item.numberValue}
                      onChange={(e) => handleNumberValueChange(index, e.target.value)}
                      placeholder="Enter measured value"
                    />
                  </div>
                )}
                {item.dataType === 'boolean' && (
                  <div className="item-value-input">
                    <label>Observed value</label>
                    <select value={item.booleanValue} onChange={(e) => handleBooleanValueChange(index, e.target.value)}>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                )}
                {item.dataType === 'select' && (
                  <div className="item-value-input">
                    <label>Observed value</label>
                    <select value={item.selectValue} onChange={(e) => handleSelectValueChange(index, e.target.value)}>
                      <option value="">Select...</option>
                      {(item.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                )}

                <div className="status-buttons">
                  <button
                    className={`status-btn compliant ${item.status === 'compliant' ? 'active' : ''}`}
                    onClick={() => handleChecklistItemChange(index, 'compliant')}
                  >
                    <i className="fas fa-check"></i> Compliant
                  </button>
                  <button
                    className={`status-btn non-compliant ${item.status === 'non-compliant' ? 'active' : ''}`}
                    onClick={() => handleChecklistItemChange(index, 'non-compliant')}
                  >
                    <i className="fas fa-times"></i> Non-Compliant
                  </button>
                  <button
                    className={`status-btn n-a ${item.status === 'n/a' ? 'active' : ''}`}
                    onClick={() => handleChecklistItemChange(index, 'n/a')}
                  >
                    <i className="fas fa-ban"></i> N/A
                  </button>
                </div>

                {item.status === 'non-compliant' && (
                  <div className={`item-value-input ${!item.severity ? 'item-value-input-required' : ''}`}>
                    <label>
                      Severity * <span className="item-target-hint">How urgent is this finding?</span>
                    </label>
                    <select value={item.severity} onChange={(e) => handleSeverityChange(index, e.target.value)}>
                      <option value="">Select severity...</option>
                      <option value="critical">Critical - severe risk, needs action right away</option>
                      <option value="major">Major - real problem, needs prompt fixing</option>
                      <option value="minor">Minor - low urgency, still worth tracking</option>
                    </select>
                    {!item.severity && item.defaultSeverity && (
                      <button
                        type="button"
                        className="btn-use-suggested-severity"
                        onClick={() => handleSeverityChange(index, item.defaultSeverity)}
                      >
                        Use this parameter's configured default: {item.defaultSeverity.charAt(0).toUpperCase() + item.defaultSeverity.slice(1)}
                      </button>
                    )}
                    {!item.severity && (
                      <span className="item-severity-required-hint">Required before you can submit this inspection.</span>
                    )}
                  </div>
                )}

                {item.status !== 'pending' && (
                  <textarea
                    className="item-notes"
                    placeholder={item.status === 'non-compliant'
                      ? 'Describe the issue - this becomes the compliance finding description...'
                      : 'Add notes or observations...'}
                    value={item.notes}
                    onChange={(e) => handleNotesChange(index, e.target.value)}
                    rows="2"
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Photo Evidence */}
      <div className="photos-section">
        <h2><i className="fas fa-camera"></i> Photo Evidence</h2>
        <div className="photo-upload-area">
          <input
            type="file"
            id="photo-upload"
            multiple
            accept="image/*"
            onChange={handlePhotoUpload}
            style={{ display: 'none' }}
          />
          <label htmlFor="photo-upload" className="upload-btn">
            <i className="fas fa-cloud-upload-alt"></i>
            Click to Upload Photos
          </label>
          <p className="upload-hint">Photos are attached to the inspection once submitted</p>
        </div>

        {photos.length > 0 && (
          <div className="photos-grid">
            {photos.map((photo, index) => (
              <div key={index} className="photo-item">
                <img src={photo.preview} alt={`Evidence ${index + 1}`} />
                <button className="remove-photo" onClick={() => removePhoto(index)}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* GPS Location */}
      <div className="gps-section">
        <h2><i className="fas fa-map-marker-alt"></i> Location Verification</h2>
        <p className="gps-hint">Capture GPS coordinates to verify inspection location</p>
        
        {!gpsLocation ? (
          <div className="gps-capture">
            <button 
              className="btn-capture-gps" 
              onClick={captureGPSLocation}
              disabled={isCapturingGPS}
            >
              {isCapturingGPS ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Capturing Location...
                </>
              ) : (
                <>
                  <i className="fas fa-crosshairs"></i> Capture GPS Coordinates
                </>
              )}
            </button>
            {gpsError && (
              <div className="gps-error">
                <i className="fas fa-exclamation-circle"></i> {gpsError}
              </div>
            )}
          </div>
        ) : (
          <div className="gps-display">
            <div className="gps-success">
              <i className="fas fa-check-circle"></i> Location Captured Successfully
            </div>
            <div className="gps-coords">
              <div className="coord-item">
                <span className="coord-label">Latitude:</span>
                <span className="coord-value">{gpsLocation.latitude.toFixed(6)}°</span>
              </div>
              <div className="coord-item">
                <span className="coord-label">Longitude:</span>
                <span className="coord-value">{gpsLocation.longitude.toFixed(6)}°</span>
              </div>
              <div className="coord-item">
                <span className="coord-label">Accuracy:</span>
                <span className="coord-value">±{Math.round(gpsLocation.accuracy)}m</span>
              </div>
              <div className="coord-item">
                <span className="coord-label">Captured:</span>
                <span className="coord-value">{new Date(gpsLocation.timestamp).toLocaleString()}</span>
              </div>
            </div>
            <button className="btn-recapture" onClick={captureGPSLocation}>
              <i className="fas fa-redo"></i> Recapture Location
            </button>
          </div>
        )}
      </div>

      {/* Overall Findings */}
      <div className="findings-section">
        <h2><i className="fas fa-file-alt"></i> Overall Findings</h2>
        <textarea
          className="findings-textarea"
          placeholder="Summarize your overall findings from this inspection..."
          value={overallFindings}
          onChange={(e) => setOverallFindings(e.target.value)}
          rows="5"
        />

        <h3>Recommendations</h3>
        <textarea
          className="findings-textarea"
          placeholder="Provide recommendations for corrective actions..."
          value={recommendations}
          onChange={(e) => setRecommendations(e.target.value)}
          rows="4"
        />

        {/* Overall Compliance Status is no longer entered manually - it's
            computed automatically from the checklist results above, so it
            can never disagree with the actual compliant/non-compliant items
            recorded for this inspection. */}
      </div>

      {/* Submit Button */}
      <div className="submit-section">
        <button className="btn-secondary" onClick={() => navigate('/inspections')}>
          Cancel
        </button>
        <button
          className="btn-primary-green"
          onClick={handleSubmit}
          disabled={isSubmitting || stats.pending > 0 || missingSeverityCount > 0}
          title={missingSeverityCount > 0 ? 'Choose a severity for every non-compliant item first' : undefined}
        >
          {isSubmitting ? (
            <>
              <i className="fas fa-spinner fa-spin"></i> Submitting...
            </>
          ) : (
            <>
              <i className="fas fa-check"></i> Submit Inspection
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default TaskExecution;
